import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateProductRecommendationTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const skinTypeId = uuidv4();
  const skinTypeBlockId = uuidv4();
  const skinConcernId = uuidv4();
  const skinConcernBlockId = uuidv4();
  const skinGoalId = uuidv4();
  const skinGoalBlockId = uuidv4();
  const routineFrequencyId = uuidv4();
  const routineFrequencyBlockId = uuidv4();
  const ingredientsId = uuidv4();
  const ingredientsBlockId = uuidv4();
  const fragranceId = uuidv4();
  const fragranceBlockId = uuidv4();
  const cleanserEndScreenId = uuidv4();
  const cleanserEndScreenBlockId = uuidv4();
  const serumEndScreenId = uuidv4();
  const serumEndScreenBlockId = uuidv4();
  const moisturizerEndScreenId = uuidv4();
  const moisturizerEndScreenBlockId = uuidv4();
  const faceMaskEndScreenId = uuidv4();
  const faceMaskEndScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const normalSkinOptionId = uuidv4();
  const oilySkinOptionId = uuidv4();
  const drySkinOptionId = uuidv4();
  const acneConcernOptionId = uuidv4();
  const agingConcernOptionId = uuidv4();
  const sensitivityConcernOptionId = uuidv4();
  const hydrationGoalOptionId = uuidv4();
  const oilControlGoalOptionId = uuidv4();
  const antiAgingGoalOptionId = uuidv4();
  const dailyRoutineOptionId = uuidv4();
  const fewTimesWeekRoutineOptionId = uuidv4();
  const occasionallyRoutineOptionId = uuidv4();
  const hyaluronicAcidOptionId = uuidv4();
  const retinolOptionId = uuidv4();
  const vitaminCOptionId = uuidv4();
  const yesFragranceOptionId = uuidv4();
  const noFragranceOptionId = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const skinConcernMediaId = uuidv4();
  const skinGoalMediaId = uuidv4();
  const ingredientsMediaId = uuidv4();
  const fragranceMediaId = uuidv4();
  const cleanserMediaId = uuidv4();
  const serumMediaId = uuidv4();
  const moisturizerMediaId = uuidv4();
  const faceMaskMediaId = uuidv4();

  // Generate new UUIDs for logic items and conditions
  const logicItem1Id = uuidv4();
  const logicItem2Id = uuidv4();
  const logicItem3Id = uuidv4();
  const condition1Id = uuidv4();
  const condition2Id = uuidv4();
  const condition3Id = uuidv4();
  const condition4Id = uuidv4();
  const condition5Id = uuidv4();
  const condition6Id = uuidv4();
  const parentLogicItem1Id = uuidv4();
  const parentLogicItem2Id = uuidv4();
  const parentLogicItem3Id = uuidv4();

  return {
    ...themes.deepRoseIvory,
    "collectionId": collectionId,
    "name": "Skincare Product Recommendation Form",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Find the perfect skincare routine for you.",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Take quiz",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "photo-1581182815808-b6eb627a8798",
          "link": "https://images.unsplash.com/photo-1581182815808-b6eb627a8798?fit=crop&q=80&w=930",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": skinTypeId,
        "blockId": skinTypeBlockId,
        "content": "What's your skin type?",
        "manditory": false,
        "order": 1,
        "selectOptions": [
          {
            "id": normalSkinOptionId,
            "order": 0,
            "value": "Normal",
            "mediaSettings": {
              "media": {
                "provider": "UNSPLASH",
                "id": `${normalSkinOptionId}-media`,
                "type": "image",
                "name": "photo-1581182800629-7d90925ad072",
                "link": "https://images.unsplash.com/photo-1581182800629-7d90925ad072?q=80&w=400",
                "workspaceId": workspaceId,
                "createdAt": new Date(),
                "updatedAt": new Date()
              }
            }
          },
          {
            "id": oilySkinOptionId,
            "order": 1,
            "value": "Oily",
            "mediaSettings": {
              "media": {
                "provider": "UNSPLASH",
                "id": `${oilySkinOptionId}-media`,
                "type": "image",
                "name": "photo-1570172619644-dfd03ed5d881",
                "link": "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?q=80&w=400",
                "workspaceId": workspaceId,
                "createdAt": new Date(),
                "updatedAt": new Date()
              }
            }
          },
          {
            "id": drySkinOptionId,
            "order": 2,
            "value": "Dry",
            "mediaSettings": {
              "media": {
                "provider": "UNSPLASH",
                "id": `${drySkinOptionId}-media`,
                "type": "image",
                "name": "photo-1670201203208-055d6d79db4a",
                "link": "https://images.unsplash.com/photo-1670201203208-055d6d79db4a?q=80&w=400",
                "workspaceId": workspaceId,
                "createdAt": new Date(),
                "updatedAt": new Date()
              }
            }
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": skinConcernId,
        "blockId": skinConcernBlockId,
        "content": "What's your main skin concern?",
        "manditory": false,
        "order": 2,
        "selectOptions": [
          {
            "id": acneConcernOptionId,
            "order": 0,
            "value": "Acne"
          },
          {
            "id": agingConcernOptionId,
            "order": 1,
            "value": "Aging/Wrinkles"
          },
          {
            "id": sensitivityConcernOptionId,
            "order": 2,
            "value": "Sensitivity"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": skinConcernMediaId,
          "type": "image",
          "name": "photo-1573461160327-b450ce3d8e7f",
          "link": "https://images.unsplash.com/photo-1573461160327-b450ce3d8e7f?q=80&w=930",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": skinGoalId,
        "blockId": skinGoalBlockId,
        "content": "What's your biggest skincare goal?",
        "manditory": false,
        "order": 3,
        "selectOptions": [
          {
            "id": hydrationGoalOptionId,
            "order": 0,
            "value": "Hydration",
          },
          {
            "id": oilControlGoalOptionId,
            "order": 1,
            "value": "Oil Control"
          },
          {
            "id": antiAgingGoalOptionId,
            "order": 2,
            "value": "Anti-Aging"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": skinGoalMediaId,
          "type": "image",
          "name": "photo-1643684391140-c5056cfd3436?q=80&w=774",
          "link": "https://images.unsplash.com/photo-1643684391140-c5056cfd3436?q=80&w=774",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": routineFrequencyId,
        "blockId": routineFrequencyBlockId,
        "content": "How often do you follow a skincare routine? ",
        "manditory": false,
        "order": 4,
        "selectOptions": [
          {
            "id": dailyRoutineOptionId,
            "order": 0,
            "value": "Daily"
          },
          {
            "id": fewTimesWeekRoutineOptionId,
            "order": 1,
            "value": "Few times a week"
          },
          {
            "id": occasionallyRoutineOptionId,
            "order": 2,
            "value": "Occasionally"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": ingredientsId,
        "blockId": ingredientsBlockId,
        "content": "What skincare ingredients interest you most? ",
        "manditory": false,
        "order": 5,
        "selectOptions": [
          {
            "id": hyaluronicAcidOptionId,
            "order": 0,
            "value": "Hyaluronic Acid"
          },
          {
            "id": retinolOptionId,
            "order": 1,
            "value": "Retinol"
          },
          {
            "id": vitaminCOptionId,
            "order": 2,
            "value": "Vitamin C"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "PEXEL",
          "id": ingredientsMediaId,
          "type": "video",
          "name": "4008670",
          "link": "https://videos.pexels.com/video-files/4008670/4008670-uhd_1440_2732_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/4008670/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "RIGHT",
        "mediaPosition": "SPLIT_LEFT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": fragranceId,
        "blockId": fragranceBlockId,
        "content": "Do you prefer fragrance-free skincare products?",
        "manditory": false,
        "order": 6,
        "selectOptions": [
          {
            "id": yesFragranceOptionId,
            "order": 0,
            "value": "Yes"
          },
          {
            "id": noFragranceOptionId,
            "order": 1,
            "value": "No"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": faceMaskEndScreenBlockId,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": fragranceMediaId,
          "type": "image",
          "name": "photo-1693004927824-f2623bbedc8b",
          "link": "https://images.unsplash.com/photo-1693004927824-f2623bbedc8b?q=80&w=774",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "horizontalAlignment": false,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": cleanserEndScreenBlockId,
            "parentJourneyItemId": fragranceId,
            "endOnCompletion": false,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition1Id,
                "value": yesFragranceOptionId,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": fragranceBlockId,
                "parentLogicItemId": parentLogicItem1Id
              },
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": "AND",
                "id": condition2Id,
                "value": hyaluronicAcidOptionId,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": ingredientsBlockId,
                "parentLogicItemId": parentLogicItem1Id
              }
            ],
          },
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem2Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": moisturizerEndScreenBlockId,
            "parentJourneyItemId": fragranceId,
            "endOnCompletion": false,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition3Id,
                "value": noFragranceOptionId,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": fragranceBlockId,
                "parentLogicItemId": parentLogicItem2Id
              },
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": "AND",
                "id": condition4Id,
                "value": vitaminCOptionId,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": ingredientsBlockId,
                "parentLogicItemId": parentLogicItem2Id
              }
            ]
          },
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": logicItem3Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": serumEndScreenBlockId,
            "parentJourneyItemId": fragranceId,
            "endOnCompletion": false,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": condition5Id,
                "value": yesFragranceOptionId,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": fragranceBlockId,
                "parentLogicItemId": parentLogicItem3Id
              },
              {
                "conditionOperation": "IS_NOT_EQUAL_TO",
                "additionalType": "AND",
                "id": condition6Id,
                "value": hyaluronicAcidOptionId,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": ingredientsBlockId,
                "parentLogicItemId": parentLogicItem3Id
              }
            ]
          }
        ]
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_INLINE_TOP",
        "id": cleanserEndScreenId,
        "blockId": cleanserEndScreenBlockId,
        "content": "We recommend The Cleanser for your skin type!",
        "manditory": false,
        "order": 7,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Buy now",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": cleanserMediaId,
          "type": "image",
          "name": "photo-1580870069867-74c57ee1bb07",
          "link": "https://images.unsplash.com/photo-1580870069867-74c57ee1bb07?q=80&w=870",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": serumEndScreenId,
        "blockId": serumEndScreenBlockId,
        "content": "We recommend The Serum for your skin type!",
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Buy now",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": serumMediaId,
          "type": "image",
          "name": "photo-1580870069867-74c57ee1bb07",
          "link": "https://images.unsplash.com/photo-1580870069867-74c57ee1bb07?q=80&w=870",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_INLINE_TOP",
        "id": moisturizerEndScreenId,
        "blockId": moisturizerEndScreenBlockId,
        "content": "We recommend The Moisturizer for your skin type!",
        "manditory": false,
        "order": 9,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Buy now",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": moisturizerMediaId,
          "type": "image",
          "name": "photo-1580870069867-74c57ee1bb07",
          "link": "https://images.unsplash.com/photo-1580870069867-74c57ee1bb07?q=80&w=870",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_INLINE_TOP",
        "id": faceMaskEndScreenId,
        "blockId": faceMaskEndScreenBlockId,
        "content": "We recommend The Face Mask for your skin type!",
        "manditory": false,
        "order": 10,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": "Buy now",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": faceMaskMediaId,
          "type": "image",
          "name": "photo-1580870069867-74c57ee1bb07",
          "link": "https://images.unsplash.com/photo-1580870069867-74c57ee1bb07?q=80&w=870",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      }
    ],
    "snippets": [],
    "metaTitle": "Skincare Product Recommendation Form",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateProductRecommendationTemplate;